import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import MediaPicker from "@/components/MediaPicker";
import { GoldButton, Pill } from "@/components/Cards";
import {
  DEPARTMENTS,
  LEADERSHIP_REGIONS,
  LEADERSHIP_ROLES,
} from "@/data/leadership";

const control =
  "min-h-12 w-full rounded-xl border border-dir-gold/20 bg-dir-elevated px-4 py-3 text-sm text-dir-text outline-none transition placeholder:text-dir-faint hover:border-dir-gold/40 focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/20";
const blank = {
  name: "",
  role_title: "",
  custom_role: "",
  department: "",
  country_region: "Global",
  description: "",
  responsibilities: "",
  linkedin_url: "",
  email: "",
  website: "",
  display_order: 1,
  featured: false,
  status: "draft",
  media: null,
};
const clean = (x) => {
  const legacyRole = x.role_title || x.title || x.position;
  return {
    ...blank,
    ...x,
    role_title: String(legacyRole || "").match(/^\d+$/) ? "" : legacyRole || "",
    display_order: Number(x.display_order ?? x.position ?? 1) || 1,
    status: x.status === "archived" ? "hidden" : x.status,
    media: x.image_url
      ? { public_url: x.image_url, alt_text: `${x.name} profile photo` }
      : null,
  };
};

export default function LeadershipManager() {
  const [items, setItems] = useState(null),
    [editing, setEditing] = useState(null),
    [q, setQ] = useState(""),
    [role, setRole] = useState(""),
    [dept, setDept] = useState(""),
    [region, setRegion] = useState(""),
    [status, setStatus] = useState("");
  const load = useCallback(
    () =>
      api
        .get("/admin/leadership")
        .then((r) => setItems(r.data.map(clean)))
        .catch(() => setItems([])),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const visible = useMemo(
    () =>
      (items || []).filter(
        (x) =>
          (!q || x.name.toLowerCase().includes(q.toLowerCase())) &&
          (!role || x.role_title === role) &&
          (!dept || x.department === dept) &&
          (!region || x.country_region === region) &&
          (!status || x.status === status),
      ),
    [items, q, role, dept, region, status],
  );
  const remove = async (x) => {
    if (!window.confirm(`Delete ${x.name}?`)) return;
    await api.delete(`/admin/leadership/${x.id}`);
    toast.success("Leadership record deleted");
    load();
  };
  const reorder = async (from, to) => {
    if (from === to) return;
    const next = [...items],
      moved = next.splice(from, 1)[0];
    next.splice(to, 0, moved);
    const ranked = next.map((x, i) => ({ ...x, display_order: i + 1 }));
    setItems(ranked);
    try {
      await Promise.all(
        ranked.map((x) => api.put(`/admin/leadership/${x.id}`, x)),
      );
      toast.success("Leadership order saved");
    } catch {
      toast.error("Could not save order");
      load();
    }
  };
  return (
    <div data-testid="leadership-manager" className="min-w-0 max-w-full">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-dir-text">Leadership</h2>
          <p className="text-sm text-dir-muted">
            Manage public leadership profiles, roles and display order.
          </p>
        </div>
        <GoldButton
          onClick={() => setEditing(blank)}
          data-testid="leadership-add"
        >
          <Plus className="h-4 w-4" />
          Add Leader
        </GoldButton>
      </div>
      <div className="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <div className="relative">
          <Search className="absolute left-3 top-4 h-4 w-4 text-dir-gold" />
          <input
            className={`${control} pl-10`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name"
          />
        </div>
        <Filter
          value={role}
          set={setRole}
          label="All roles"
          options={[...new Set(Object.values(LEADERSHIP_ROLES).flat())]}
        />
        <Filter
          value={dept}
          set={setDept}
          label="All departments"
          options={DEPARTMENTS}
        />
        <Filter
          value={region}
          set={setRegion}
          label="All regions"
          options={LEADERSHIP_REGIONS}
        />
        <Filter
          value={status}
          set={setStatus}
          label="All statuses"
          options={["draft", "published", "hidden"]}
        />
      </div>
      {!items ? (
        <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />
      ) : (
          <div className="max-w-full overflow-x-auto rounded-2xl border border-dir-gold/15">
          <table className="min-w-[960px] w-full text-sm">
            <thead className="bg-dir-deep text-left text-dir-faint">
              <tr>
                {[
                  "",
                  "Photo",
                  "Name",
                  "Role",
                  "Department",
                  "Country / Region",
                  "Status",
                  "Order",
                  "Actions",
                ].map((x) => (
                  <th key={x} className="p-3">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((x, i) => (
                <tr
                  key={x.id}
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData(
                      "text/plain",
                      String(items.indexOf(x)),
                    )
                  }
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) =>
                    reorder(
                      Number(e.dataTransfer.getData("text/plain")),
                      items.indexOf(x),
                    )
                  }
                  className="border-t border-dir-gold/10 bg-dir-card/30 hover:bg-dir-card"
                >
                  <td className="p-3">
                    <GripVertical className="h-4 w-4 cursor-grab text-dir-faint" />
                  </td>
                  <td className="p-3">
                    {x.image_url ? (
                      <img
                        src={x.image_url}
                        alt=""
                        className="h-12 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <Initials name={x.name} />
                    )}
                  </td>
                  <td className="p-3 font-medium text-dir-text">{x.name}</td>
                  <td className="p-3 text-dir-muted">
                    {x.custom_role || x.role_title || "Needs review"}
                  </td>
                  <td className="p-3 text-dir-muted">{x.department || "—"}</td>
                  <td className="p-3 text-dir-muted">
                    {x.country_region || "—"}
                  </td>
                  <td className="p-3">
                    <Pill>{x.status}</Pill>
                  </td>
                  <td className="p-3 text-dir-muted">{x.display_order}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        aria-label={`Edit ${x.name}`}
                        onClick={() => setEditing(x)}
                        className="min-h-11 min-w-11 rounded-lg border border-dir-gold/25 p-2 text-dir-gold"
                      >
                        <Pencil className="mx-auto h-4 w-4" />
                      </button>
                      <button
                        aria-label={`Delete ${x.name}`}
                        onClick={() => remove(x)}
                        className="min-h-11 min-w-11 rounded-lg border border-red-500/25 p-2 text-red-400"
                      >
                        <Trash2 className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <LeadershipForm
          value={editing}
          close={() => setEditing(null)}
          saved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
function Filter({ value, set, label, options }) {
  return (
    <select
      className={control}
      value={value}
      onChange={(e) => set(e.target.value)}
    >
      <option value="">{label}</option>
      {options.map((x) => (
        <option key={x}>{x}</option>
      ))}
    </select>
  );
}
function Initials({ name }) {
  return (
    <div className="flex h-12 w-10 items-center justify-center rounded-lg bg-dir-deep text-xs font-bold text-dir-gold">
      {(name || "DIR")
        .split(/\s+/)
        .slice(0, 2)
        .map((x) => x[0])
        .join("")}
    </div>
  );
}
function LeadershipForm({ value, close, saved }) {
  const [f, setF] = useState(clean(value)),
    [saving, setSaving] = useState(false);
  const patch = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        image_url: f.media?.public_url || f.media?.url || "",
        display_order: Math.max(1, Math.floor(Number(f.display_order) || 1)),
      };
      if (f.id) await api.put(`/admin/leadership/${f.id}`, payload);
      else await api.post("/admin/leadership", payload);
      toast.success("Leadership profile saved");
      saved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 p-3 sm:p-6"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leadership-editor-title"
        className="mx-auto flex max-h-[calc(100vh-24px)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-dir-gold/20 bg-dir-bg shadow-2xl sm:max-h-[calc(100vh-48px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-dir-gold/15 bg-dir-deep px-5 py-4 sm:px-7">
          <div>
            <h2
              id="leadership-editor-title"
              className="font-display text-xl text-dir-text"
            >
              {f.id ? "Edit" : "Add"} Leadership Profile
            </h2>
            <p className="text-xs text-dir-muted">
              Public profile and organizational placement
            </p>
          </div>
          <button
            type="button"
            aria-label="Close editor"
            onClick={close}
            className="min-h-11 min-w-11 rounded-xl p-2 text-dir-muted focus:ring-2 focus:ring-dir-gold/30"
          >
            <X className="mx-auto" />
          </button>
        </header>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-7">
            <MediaPicker
              label="Profile Photo"
              folder="leadership"
              accept="image/jpeg,image/png,image/webp"
              allowExternal={false}
              value={f.media}
              onChange={(m) => patch("media", m)}
            />
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <Field label="Full Name">
                <input
                  required
                  className={control}
                  value={f.name}
                  onChange={(e) => patch("name", e.target.value)}
                />
              </Field>
              <RolePicker
                value={f.role_title}
                onChange={(v) => {
                  patch("role_title", v);
                  if (v !== "Other / Custom Role") patch("custom_role", "");
                }}
              />
              {f.role_title === "Other / Custom Role" && (
                <Field label="Custom Role">
                  <input
                    required
                    className={control}
                    value={f.custom_role}
                    onChange={(e) => patch("custom_role", e.target.value)}
                  />
                </Field>
              )}
              <Field label="Department / Function">
                <select
                  className={control}
                  value={f.department}
                  onChange={(e) => patch("department", e.target.value)}
                >
                  <option value="">Select department</option>
                  {DEPARTMENTS.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <ChoicePicker
                label="Country / Region"
                value={f.country_region}
                onChange={(v) => patch("country_region", v)}
                options={LEADERSHIP_REGIONS}
              />
              <Field
                wide
                label="Short Professional Bio"
                hint={`${f.description.length}/600 · 250–600 characters recommended`}
              >
                <textarea
                  className={control}
                  rows="5"
                  maxLength="1200"
                  value={f.description}
                  onChange={(e) => patch("description", e.target.value)}
                />
              </Field>
              <Field
                wide
                label="Responsibilities / Scope"
                hint="Enter one concise responsibility per line"
              >
                <textarea
                  className={control}
                  rows="5"
                  value={f.responsibilities}
                  onChange={(e) => patch("responsibilities", e.target.value)}
                  placeholder={
                    "Strategic leadership\nProgram oversight\nInstitutional partnerships"
                  }
                />
              </Field>
              <Field label="LinkedIn">
                <input
                  className={control}
                  type="url"
                  value={f.linkedin_url}
                  onChange={(e) => patch("linkedin_url", e.target.value)}
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  className={control}
                  type="email"
                  value={f.email}
                  onChange={(e) => patch("email", e.target.value)}
                />
              </Field>
              <Field label="Website (optional)">
                <input
                  className={control}
                  type="url"
                  value={f.website}
                  onChange={(e) => patch("website", e.target.value)}
                />
              </Field>
              <Field label="Display Order" hint="Lower number appears first">
                <input
                  className={control}
                  type="number"
                  min="1"
                  step="1"
                  value={f.display_order}
                  onChange={(e) => patch("display_order", e.target.value)}
                />
              </Field>
              <Field label="Featured Leadership">
                <label className="flex min-h-12 items-center gap-3 rounded-xl border border-dir-gold/20 bg-dir-elevated px-4 text-sm text-dir-text">
                  <input
                    type="checkbox"
                    checked={f.featured}
                    onChange={(e) => patch("featured", e.target.checked)}
                    className="h-4 w-4 accent-dir-gold"
                  />
                  Feature this leader
                </label>
              </Field>
              <Field label="Status">
                <select
                  className={control}
                  value={f.status}
                  onChange={(e) => patch("status", e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="hidden">Hidden</option>
                </select>
              </Field>
            </div>
          </div>
          <footer className="flex shrink-0 justify-end gap-3 border-t border-dir-gold/15 bg-dir-deep px-5 py-4 sm:px-7">
            <button
              type="button"
              onClick={close}
              className="min-h-12 rounded-full border border-dir-gold/30 px-6 text-sm text-dir-muted"
            >
              Cancel
            </button>
            <GoldButton disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save Changes"
              )}
            </GoldButton>
          </footer>
        </form>
      </div>
    </div>
  );
}
function Field({ label, hint, wide, children }) {
  return (
    <label className={wide ? "md:col-span-2" : ""}>
      <span className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-dir-muted">
        <span>{label}</span>
        {hint && (
          <span className="normal-case tracking-normal text-dir-faint">
            {hint}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
function RolePicker({ value, onChange }) {
  return (
    <ChoicePicker
      label="Role / Title"
      value={value}
      onChange={onChange}
      groups={LEADERSHIP_ROLES}
      placeholder="Search professional roles"
    />
  );
}
function ChoicePicker({
  label,
  value,
  onChange,
  options = [],
  groups,
  placeholder = "Search options",
}) {
  const [open, setOpen] = useState(false),
    [q, setQ] = useState("");
  const data = groups || { Options: options };
  const filtered = Object.entries(data)
    .map(([g, x]) => [
      g,
      x.filter((v) => v.toLowerCase().includes(q.toLowerCase())),
    ])
    .filter(([, x]) => x.length);
  return (
    <div className="relative">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-dir-muted">
        {label}
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
        className={`${control} flex items-center justify-between text-left`}
      >
        <span>{value || `Select ${label.toLowerCase()}`}</span>
        <ChevronDown className="h-4 w-4 text-dir-gold" />
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-2xl border border-dir-gold/25 bg-dir-deep p-3 shadow-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-dir-gold" />
            <input
              autoFocus
              className={`${control} pl-10`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
            />
          </div>
          <div role="listbox" className="mt-2 max-h-64 overflow-y-auto">
            {filtered.map(([g, x]) => (
              <div key={g}>
                <div className="sticky top-0 bg-dir-deep px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-dir-gold">
                  {g}
                </div>
                {x.map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="option"
                    aria-selected={v === value}
                    onClick={() => {
                      onChange(v);
                      setOpen(false);
                      setQ("");
                    }}
                    className="block min-h-11 w-full rounded-lg px-3 py-2 text-left text-sm text-dir-muted hover:bg-dir-gold/10 hover:text-dir-text focus:bg-dir-gold/10 focus:outline-none"
                  >
                    {v}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
